import { format } from 'date-fns-tz';
import { mutate } from 'swr';
import type { StateCreator } from 'zustand';
import {
  deletePlayerAccount,
  player as fetchPlayer,
  playerAccounts as fetchPlayerAccounts,
  logout as logoutRequest,
  setPlayerAccounts,
  type User,
  type Wallet,
} from '@/api/auth';
import { compPoints as fetchCompPoints } from '@/api/compPoints';
import { PLAYER_ACCOUNTS_KEY } from '@/helpers/constants';
import {
  currencyFlags,
  DEFAULT_CURRENCIES,
  isCryptoCurrency,
  mapCurrencyName,
} from '@/helpers/currency';
import { setCustomerIOOnline } from '@/helpers/customerio';
import { resetCachePayments } from '@/helpers/payments';
import { setGroupsForNewcomer } from '@/helpers/user';
import { saveUserData } from '@/helpers/userData';
import {
  getCurrencyCentsAndCode,
  mergeAccountByCurrency,
  mergeBalanceIntoWallets,
} from '@/helpers/wallet';
import { resetSignOutTracked } from '@/modules/common/helpers/handleSignOutEvent';
import type {
  IAccount,
  IPlayerBalance,
} from '@/modules/common/hooks/usePlayerAccounts';
import { resolveRiskSegmentClient } from '@/modules/riskSegment/resolveRiskSegmentClient';
import { clearMarketingAttribution } from '@/modules/userTrackingParams/helpers/clearMarketingAttributionHelper';
import { STORAGE, useLocalStorageStore } from '@/store/localStorageStore';
import type { AuthState, CommonStoreType, UserState } from '../../types';
import {
  PersistentFormName,
  usePersistentFormsSlice,
} from '../persistant/usePersistentFormsSlice';
import { REASON_FOR_UPDATING_USER } from './helpers';

export const initialState: UserState = {
  user: undefined,
  guestLanguage: undefined,
  reasonForUpdatingUser: undefined,
  isAuthenticated: false,
  pendingUser: false,
  pendingUserPure: false,
  wallets: [],
  pendingWallets: false,
  afterLoginRedirectUrl: null,
  afterAdditionalDataRedirectUrl: null,
  afterRegistrationRedirectUrl: null,
  afterDepositRedirectUrl: null,
};

export const useAuthSlice: StateCreator<CommonStoreType, [], [], AuthState> = (
  set,
  getState
) => {
  return {
    ...initialState,
    extendedUserWallets: [],
    currentWallet: null,
    isEmailConfirmationPopup: false,
    emailConfirmationPopupHeight: 0,
    limits: [],

    fetchUser: async (reasonForUpdatingUser: REASON_FOR_UPDATING_USER) => {
      const { countryCode, setUserWallets } = getState();

      try {
        if (reasonForUpdatingUser !== REASON_FOR_UPDATING_USER.VISIT) {
          set({
            pendingUser: true,
          });
        }

        const { data: user } = await fetchPlayer();

        const isAuthenticated = user?.id != null;

        // Passive logged out during offline
        sessionStorage.setItem(IS_USER_FETCHED_FIRST_TIME, 'true');
        //

        if (isAuthenticated) {
          resetSignOutTracked();

          set({
            pendingWallets: true,
            compPointsPending: true,
          });

          let wallets: Wallet[] = [];

          const [{ data: dataWallets }, { data: compPoints }] =
            await Promise.all([fetchPlayerAccounts(), fetchCompPoints()]);

          wallets = dataWallets || [];

          if (wallets.length === 0 && user?.currency != null) {
            const { data: dataUpdatedWallet } = await setPlayerAccounts(
              user.currency
            );

            if (dataUpdatedWallet) {
              wallets = [dataUpdatedWallet];
            }
          }

          saveUserData({ email: user.email });

          set({
            user: setGroupsForNewcomer(user, countryCode),
            reasonForUpdatingUser,
            wallets,
            isAuthenticated,
            compPoints,
          });
          setUserWallets(wallets);

          if (
            reasonForUpdatingUser === REASON_FOR_UPDATING_USER.LOGIN ||
            reasonForUpdatingUser === REASON_FOR_UPDATING_USER.REGISTRATION
          ) {
            resolveRiskSegmentClient()
              .then((riskSegment) => set({ riskSegment }))
              .catch(() => {});
          }

          return user;
        }

        set({
          ...initialState,
          guestLanguage: user?.language,
          reasonForUpdatingUser,
        });
      } catch {
        set({
          ...initialState,
          reasonForUpdatingUser,
        });
      } finally {
        set({
          pendingUser: false,
          pendingUserPure: false,
          pendingWallets: false,
          compPointsPending: false,
        });
      }
    },
    updateUser: (user: Partial<User>) => {
      const { user: prevUser } = getState();
      if (!prevUser) return;
      set({ user: { ...prevUser, ...user } });
    },
    refreshUser: async () => {
      const { user: prevUser, countryCode } = getState();
      try {
        const { data: user } = await fetchPlayer();

        if (
          JSON.stringify(prevUser?.statuses) === JSON.stringify(user?.statuses)
        )
          return;
        const updatedUser = setGroupsForNewcomer(user, countryCode);
        updatedUser && set({ user: updatedUser });
      } catch (error) {
        console.log(error);
      }
    },
    insertUser: async (
      user: User,
      reasonForUpdatingUser: REASON_FOR_UPDATING_USER
    ) => {
      const { setUserWallets } = getState();

      saveUserData({ email: user.email });

      set({ pendingWallets: true });

      try {
        let wallets: Wallet[] = [];
        const [{ data: dataWallets }, { data: compPoints }] = await Promise.all(
          [fetchPlayerAccounts(), fetchCompPoints()]
        );

        wallets = dataWallets || [];

        if (wallets.length === 0 && user?.currency != null) {
          const { data: dataUpdatedWallet } = await setPlayerAccounts(
            user.currency
          );

          if (dataUpdatedWallet) {
            wallets = [dataUpdatedWallet];
          }
        }

        set({
          user: setGroupsForNewcomer(user),
          reasonForUpdatingUser,
          isAuthenticated: user.id != null,
          compPoints,
        });

        if (user.id != null) {
          resetSignOutTracked();
        }

        setUserWallets(wallets);

        if (
          reasonForUpdatingUser === REASON_FOR_UPDATING_USER.LOGIN ||
          reasonForUpdatingUser === REASON_FOR_UPDATING_USER.REGISTRATION
        ) {
          resolveRiskSegmentClient()
            .then((riskSegment) => set({ riskSegment }))
            .catch(() => {});
        }
      } catch (error) {
        console.log(error);
      } finally {
        set({ pendingWallets: false });
      }
    },
    resetUser: async (callback?: () => void) => {
      try {
        set({ pendingUser: true });
        // Must run before the state reset: afterwards the player id is gone.
        const playerId = getState().user?.id;
        if (playerId) setCustomerIOOnline(playerId, false);
        await logoutRequest();
        mutate('/notification_center/centrifugo/subscription', undefined, {
          revalidate: false,
        });
        mutate(PLAYER_ACCOUNTS_KEY, undefined, { revalidate: false });

        set({
          ...initialState,
          recentGamesIds: [],
          recentGames: [],
        });

        clearMarketingAttribution();

        resetCachePayments().catch(() => null);
        const { resetPersistentForm } = usePersistentFormsSlice.getState();
        resetPersistentForm(PersistentFormName.PaymentMethodPersonalInfo);
        resetPersistentForm(PersistentFormName.WithdrawPersonalInfo);

        callback && callback();
      } catch (error) {
        console.log(error);
      } finally {
        set({ pendingUser: false, pendingWallets: false });
      }
    },

    setUserWallets: async (wallets: Wallet[]) => {
      const { user } = getState();

      const extendedUserWallets = wallets.map((wallet) => ({
        ...wallet,
        amountCents: getCurrencyCentsAndCode(
          wallet.amountCents,
          wallet.currency,
          DEFAULT_CURRENCIES
        ).amount,
        subCurrency: getCurrencyCentsAndCode(
          wallet.amountCents,
          wallet.currency,
          DEFAULT_CURRENCIES
        ).currency,
        name: mapCurrencyName[wallet.currency],
        isCrypto: isCryptoCurrency(DEFAULT_CURRENCIES, wallet.currency),
        icon: currencyFlags[wallet.currency],
      }));

      const currentWallet =
        extendedUserWallets.find(
          ({ currency }) => currency === user?.currency
        ) || extendedUserWallets[0];

      set({ wallets, extendedUserWallets, currentWallet });
    },
    changeUserWallet: async (currency: Wallet['currency']) => {
      const { fetchUser } = getState();

      try {
        set({ pendingWallets: true });
        await setPlayerAccounts(currency);
        await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
      } catch (error) {
        console.log(error);
      } finally {
        set({ pendingWallets: false });
      }
    },
    deleteUserWallet: async (
      prevCurrency: Wallet['currency'],
      nextCurrency?: Wallet['currency']
    ) => {
      const { fetchUser } = getState();

      try {
        set({ pendingWallets: true });
        await deletePlayerAccount(prevCurrency);
        nextCurrency && (await setPlayerAccounts(nextCurrency));
        await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
      } catch {
        set({ pendingWallets: false });
      }
    },
    setEmailConfirmationPopupSeen: () => {
      useLocalStorageStore
        .getState()
        .replace(
          STORAGE.LAST_SEEN_EMAIL_CONFIRMATION_POPUP,
          format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX")
        );
      set({ isEmailConfirmationPopup: false });
    },
    setEmailConfirmationPopupHeight: (height: number) => {
      set({ emailConfirmationPopupHeight: height });
    },
    updatePlayerWallet: async (currency, user) => {
      try {
        set({ pendingWallets: true });

        const { data: wallet } = await setPlayerAccounts(currency);

        if (!wallet) return;

        const { setUserWallets, wallets } = getState();

        set({
          user: { ...user, currency },
        });

        const isSameCurrency = (w: Wallet) => w.currency === wallet.currency;

        const mergedWallets = wallets.some(isSameCurrency)
          ? wallets.map((w) => (isSameCurrency(w) ? wallet : w))
          : [...wallets, wallet];

        setUserWallets(mergedWallets);
      } catch (error) {
        console.log(error);
      } finally {
        set({ pendingWallets: false });
      }
    },
    updatePlayerBalance: (balance: IPlayerBalance) => {
      mutate<IAccount[]>(
        PLAYER_ACCOUNTS_KEY,
        (accounts) =>
          accounts?.length
            ? mergeAccountByCurrency<IAccount>(accounts, balance)
            : accounts,
        { revalidate: false }
      );

      const { wallets, setUserWallets } = getState();
      const mergedWallets = mergeBalanceIntoWallets(wallets, balance);

      if (mergedWallets !== wallets) setUserWallets(mergedWallets);
    },
  };
};

const IS_USER_FETCHED_FIRST_TIME = 'is_user_fetched_first_time';
